import { CONSTANTS } from '../constants/constants';

const { KEYS, STATUS, PARTS_USAGE_ANSWER } = CONSTANTS;
const VALID_ANSWERS = Object.values(PARTS_USAGE_ANSWER);

export const isPartsUsageTask = (task) => task?.key === KEYS.RECORD_PARTS_USAGE;

// A parts task the server echoed back without its answers has nothing to show, edit or require.
export const hasPartsUsageData = (task) => Array.isArray(task?.taskData?.partsUsage?.parts);

// The SOW quantity comes from the server: an implausible one must degrade to a single line rather
// than build thousands of inputs or throw a RangeError from Array.from.
export const MAX_UNITS_PER_PART = 500;

const unitsOf = (component) => {
  const quantity = Math.floor(Number(component?.quantity));
  return Number.isFinite(quantity) && quantity > 1 && quantity <= MAX_UNITS_PER_PART ? quantity : 1;
};

const identityOf = (component) => `${component.code ?? ''}|${component.partNumber ?? ''}|${component.serial ?? ''}`;

// Units are numbered per identity (code, part number, serial), as the server groups them, across
// rows, so two rows for the same part never share a lineKey. A line's key depends only on its own
// identity and unit, never on where its row sits, so reordering rows keeps the answers attached.
// One expansion serves both the task snapshot and the Components tab, keeping their keys in step.
const expandComponents = (components) => {
  const counters = new Map();
  // A null entry in the server's list must not take the whole task list down with it
  return (Array.isArray(components) ? components : []).filter(Boolean).map((component) => {
    const identity = identityOf(component);
    const lines = Array.from({ length: unitsOf(component) }, () => {
      const unit = (counters.get(identity) ?? 0) + 1;
      counters.set(identity, unit);
      return {
        lineKey: `${identity}|${unit}`,
        code: component.code ?? '',
        partNumber: component.partNumber ?? '',
        name: component.name ?? '',
        serial: component.serial ?? '',
        unit,
        answer: null,
      };
    });
    return { component, lines };
  });
};

export const buildPartsUsage = (components) => ({
  notApplicable: false,
  parts: expandComponents(components).flatMap(({ lines }) => lines),
});

export const isPartsUsageComplete = (partsUsage) => {
  if (!partsUsage) return false;
  if (partsUsage.notApplicable) return true;
  const { parts = [] } = partsUsage;
  return parts.length > 0 && parts.every((part) => VALID_ANSWERS.includes(part.answer));
};

export const applyPartAnswer = (partsUsage, lineKey, answer) => {
  // Not applicable locks the answers: they would contradict it in the submitted payload
  if (partsUsage.notApplicable) return partsUsage;
  if (answer !== null && !VALID_ANSWERS.includes(answer)) return partsUsage;
  return {
    ...partsUsage,
    parts: partsUsage.parts.map((part) => (part.lineKey === lineKey ? { ...part, answer } : part)),
  };
};

export const applyNotApplicable = (partsUsage, notApplicable) => ({
  ...partsUsage,
  notApplicable,
  parts: partsUsage.parts.map((part) => ({ ...part, answer: null })),
});

// This task has no timer: it is complete exactly when its answers are.
export const withDerivedCompletion = (task) => {
  const isComplete = isPartsUsageComplete(task.taskData?.partsUsage);
  return {
    ...task,
    completed: isComplete,
    status: isComplete ? STATUS.COMPLETED : STATUS.NOT_STARTED,
  };
};

export const createPartsUsageTask = (components, t) => ({
  id: 0,
  description: t('recordPartsUsage'),
  taskData: { description: t('recordPartsUsage'), partsUsage: buildPartsUsage(components) },
  completed: false,
  timeSpent: 0,
  status: STATUS.NOT_STARTED,
  startTime: null,
  endTime: null,
  location: null,
  submitted: false,
  paused: false,
  key: KEYS.RECORD_PARTS_USAGE,
});

// Tickets opened before this task existed already have their tasks stored, and the parser is not
// re-run for them. Only tickets with a Client QC built-in are touched, and submitted ones never.
export const canBackfillPartsUsage = (tasks) =>
  tasks.some((task) => task.key === KEYS.CUSTOMER_QA)
  && !tasks.some((task) => isPartsUsageTask(task) || task.submitted);

export const backfillPartsUsageTask = (tasks, components, t) => {
  const hasComponents = Array.isArray(components) && components.length > 0;
  if (!hasComponents || !canBackfillPartsUsage(tasks)) return tasks;
  const insertAt = tasks.findIndex((task) => task.key === KEYS.CUSTOMER_QA);

  const now = Date.now();
  const withTask = [...tasks.slice(0, insertAt), createPartsUsageTask(components, t), ...tasks.slice(insertAt)];
  return withTask.map((task, index) => {
    const id = index + 1;
    // Only the new task is an edit. A renumbered task keeps its own edit time, so a newer edit made to
    // it in another tab while the work order data was loading still wins when the copies reconcile.
    if (isPartsUsageTask(task)) return { ...task, id, lastModified: now };
    return task.id !== id ? { ...task, id } : task;
  });
};

// The parts list is a snapshot taken when the task was created. If the work order's parts changed
// since, rebuild it (keeping the answers of lines that still exist) so the task always covers every
// unit the server expects. Submitted tasks and an unknown parts list (no array) are left alone.
// A work order that now has no parts retires the task as Not applicable instead of deleting it: a
// deleted keyed task would be adopted back from a stale copy when the tabs' records reconcile.
export const syncPartsUsageTask = (tasks, components) => {
  if (!Array.isArray(components)) return tasks;
  let changed = false;
  const synced = tasks.map((task) => {
    if (!isPartsUsageTask(task) || task.submitted || !hasPartsUsageData(task)) return task;
    const { partsUsage } = task.taskData;
    const answers = new Map(partsUsage.parts.map((part) => [part.lineKey, part.answer ?? null]));
    const rebuilt = buildPartsUsage(components).parts.map((part) => ({ ...part, answer: answers.get(part.lineKey) ?? null }));
    // Parts coming back to a retired task start unanswered, not as Not applicable
    const notApplicable = components.length === 0 || (partsUsage.parts.length > 0 && Boolean(partsUsage.notApplicable));
    const sameKeys = rebuilt.length === partsUsage.parts.length
      && rebuilt.every((part, index) => part.lineKey === partsUsage.parts[index].lineKey);
    if (sameKeys && notApplicable === Boolean(partsUsage.notApplicable)) return task;
    changed = true;
    return withDerivedCompletion({
      ...task,
      taskData: { ...task.taskData, partsUsage: { ...partsUsage, notApplicable, parts: rebuilt } },
      lastModified: Date.now(),
    });
  });
  return changed ? synced : tasks;
};

// Component object -> its lines (with the recorded answers), for views that list components.
export const linesForComponents = (components, partsUsage) => {
  const answers = new Map((partsUsage?.parts ?? []).map((part) => [part.lineKey, part.answer ?? null]));
  if (!partsUsage) return new Map();
  return new Map(
    expandComponents(components).map(({ component, lines }) => [
      component,
      lines.map((line) => ({ ...line, answer: answers.get(line.lineKey) ?? null })),
    ])
  );
};
